<template>
    <div>
        <!-- Sauvegarde / Chargement config -->
        <config-save-load ref="configSaveLoad" :value="value" type="chart" :context="context" @input="$emit('input', $event)" class="mb-1" />

        <!-- Génération IA -->
        <template v-if="tableStructure && tableStructure.length > 0 && _canGenerateIa">
            <hr class="my-2" />
            <prompt-generator
                :build-request="buildAiRequest"
                :parse-result="parseAiResult"
                :validate-result="validateAiResult"
                :json-schema="chartJsonSchema"
                title="Demander à ChatGPT de configurer ce graphique"
                modal-title="Générer la configuration du graphique"
                prompt-placeholder="ex: je veux les ventes regroupées par jour"
                button-variant="outline-primary"
                button-icon="fas fa-magic"
                :button-label="aiButtonLabel"
                @result="onAiResult"
            >
                <template #options>
                    <div class="mb-3">
                        <b-checkbox v-model="aiIncludeExamples">
                            Envoyer la colonne Exemple des colonnes
                        </b-checkbox>
                        <b-checkbox v-model="aiSendCurrentConfig" class="mt-1">
                            Envoyer la configuration actuelle (modification)
                        </b-checkbox>
                    </div>
                </template>
            </prompt-generator>
        </template>

        <!-- ── DONNÉES ──────────────────────────────────────── -->
        <hr class="my-2" />
        <small class="text-uppercase text-muted font-weight-bold">Données</small>

        <b-row class="mt-2 no-gutters" style="gap:0.5rem">
            <b-col>
                <label class="mb-1 small">Type</label>
                <b-select :value="value.type"
                    :options="[{value:'pie',text:'Camembert'},{value:'line',text:'Courbe'},{value:'area',text:'Aires'},{value:'column',text:'Barres'},{value:'polar',text:'Aire polaire'},{value:'radar',text:'Radar'},{value:'bubble',text:'Bulles'}]"
                    @change="emit('type', $event)" />
            </b-col>
            <b-col v-if="value.type !== 'bubble'">
                <label class="mb-1 small">Source</label>
                <b-select :value="value.data_mode"
                    :options="[
                        {value:'count', text:'Compter les lignes'},
                        {value:'sum',   text:'Additionner les valeurs d\'une colonne'},
                        {value:'value', text:'Valeurs brutes (pas de regroupement)'}
                    ]"
                    @change="emit('data_mode', $event)" />
            </b-col>
        </b-row>

        <div class="mt-3">
            <label class="mb-1 small">
                {{ value.type === 'bubble' ? 'Colonne X' : (value.data_mode === 'value' ? 'Colonne libellé' : 'Colonne de regroupement') }}
                <span class="text-muted">{{ value.type === 'bubble' ? '— axe X (numérique)' : (value.data_mode === 'value' ? (value.type === 'pie' ? '— libellé de chaque tranche' : '— axe X') : (value.type === 'column' ? '— une barre par valeur distincte' : (value.type === 'pie' ? '— une tranche par valeur distincte' : '— un point par valeur distincte'))) }}</span>
            </label>
            <b-form-input :value="value.label_col" placeholder="ex: categorie, date..."
                :list="'chart-label-' + uid"
                @input="emit('label_col', $event)" />
            <b-form-datalist :id="'chart-label-' + uid" :options="columns" />

            <!-- Troncature (count / sum uniquement) -->
            <div v-show="value.data_mode === 'count' || value.data_mode === 'sum'" class="mt-1 pl-2 border-left">
                <b-checkbox :checked="value.date_trunc" @change="emit('date_trunc', $event)">
                    Tronquer les dates
                </b-checkbox>
                <div v-show="value.date_trunc" class="mt-1">
                    <b-select :value="value.date_trunc_unit" size="sm"
                        :options="[
                            {value:'minute', text:'Par minute  (YYYY-MM-DD HH:MM)'},
                            {value:'hour',   text:'Par heure   (YYYY-MM-DD HH:00)'},
                            {value:'day',    text:'Par jour    (YYYY-MM-DD)'},
                            {value:'month',  text:'Par mois    (YYYY-MM)'},
                            {value:'year',   text:'Par année   (YYYY)'}
                        ]"
                        @change="emit('date_trunc_unit', $event)" />
                </div>
            </div>

            <!-- Colonne séries (courbe uniquement) -->
            <div v-if="value.type !== 'pie' && value.type !== 'polar'" class="mt-1 pl-2 border-left">
                <label class="mb-1 small">
                    Colonne séries
                    <span class="text-muted">— optionnel, une courbe par valeur</span>
                </label>
                <b-form-input :value="value.series_col" placeholder="ex: categorie, statut..."
                    :list="'chart-series-' + uid"
                    @input="emit('series_col', $event)" />
                <b-form-datalist :id="'chart-series-' + uid" :options="columns" />
            </div>
        </div>

        <!-- Colonne valeurs (sum / value / radar / polar / bubble) -->
        <div v-if="value.data_mode === 'sum' || value.data_mode === 'value' || value.type === 'radar' || value.type === 'polar' || value.type === 'bubble'" class="mt-2">
            <label class="mb-1 small">
                Colonne valeurs
                <span class="text-muted">{{ value.type !== 'pie' && value.type !== 'polar' ? '— axe Y' : (value.data_mode === 'sum' ? '— valeur à additionner' : '— valeur de chaque ligne') }}</span>
            </label>
            <b-form-input :value="value.value_col" placeholder="ex: montant, count..."
                :list="'chart-value-' + uid"
                @input="emit('value_col', $event)" />
            <b-form-datalist :id="'chart-value-' + uid" :options="columns" />
        </div>

        <!-- Rayon des bulles (bubble uniquement) -->
        <div v-if="value.type === 'bubble'" class="mt-2">
            <label class="mb-1 small">
                Colonne rayon
                <span class="text-muted">— taille de chaque bulle</span>
            </label>
            <b-form-input :value="value.radius_col" placeholder="ex: montant, score..."
                :list="'chart-radius-' + uid"
                @input="emit('radius_col', $event)" />
            <b-form-datalist :id="'chart-radius-' + uid" :options="columns" />
        </div>

        <!-- Libellé des bulles (bubble uniquement) -->
        <div v-if="value.type === 'bubble'" class="mt-2">
            <label class="mb-1 small">
                Colonne libellé
                <span class="text-muted">— affiché dans le tooltip</span>
            </label>
            <b-form-input :value="value.bubble_label_col" placeholder="ex: nom, ref..."
                :list="'chart-blabel-' + uid"
                @input="emit('bubble_label_col', $event)" />
            <b-form-datalist :id="'chart-blabel-' + uid" :options="columns" />
        </div>

        <!-- Options camembert -->
        <div v-if="value.type === 'pie'" class="mt-2">
            <label class="mb-1 small">
                Tranches max
                <span class="text-muted">— les suivantes sont regroupées en "Autres"</span>
            </label>
            <b-form-input type="number" min="2" max="100"
                :value="value.pie_max_slices || 15"
                @input="emit('pie_max_slices', $event ? parseInt($event) : 25)" />
        </div>

        <!-- ── AFFICHAGE ─────────────────────────────────────── -->
        <hr class="my-2" />
        <small class="text-uppercase text-muted font-weight-bold">Affichage</small>

        <b-checkbox v-if="value.type === 'line' || value.type === 'area' || value.type === 'radar'" :checked="value.smooth !== false" @change="emit('smooth', $event)" class="mt-2">
            Lisser la courbe
        </b-checkbox>
        <b-checkbox v-if="value.type === 'column' || value.type === 'area'" :checked="!!value.stacked" @change="emit('stacked', $event)" class="mt-2">
            Superposer les séries
        </b-checkbox>

        <div class="mt-2">
            <label class="mb-1 small">Titre <span class="text-muted">— affiché dans le graphique</span></label>
            <b-input :value="value.title" @input="emit('title', $event)" />
        </div>
        <div class="mt-2">
            <label class="mb-1 small">Texte <span class="text-muted">— affiché au-dessus du graphique</span></label>
            <b-textarea :value="value.pre_text" rows="2" @input="emit('pre_text', $event)" />
        </div>
        <div class="mt-2">
            <label class="mb-1 small">Commentaire interne <span class="text-muted">— note, non affiché</span></label>
            <b-textarea :value="value.comment" rows="2" @input="emit('comment', $event)" />
        </div>

        <!-- ── TRANSFORMATION JS ─────────────────────────────── -->
        <hr class="my-2" />
        <b-checkbox :checked="transformEnabled" @change="onToggleTransform" class="font-weight-bold">
            <small class="text-uppercase text-muted font-weight-bold">Transformation JS</small>
        </b-checkbox>
        <template v-if="transformEnabled">
            <div class="mt-2">
                <label class="mb-1 small">Mode</label>
                <b-select :value="value.transform_mode || 'row'"
                    :options="[
                        {value:'row', text:'Par ligne  — data = un objet ligne'},
                        {value:'all', text:'Toutes les données — data = tableau de lignes (retourner le nouveau tableau)'}
                    ]"
                    @change="emit('transform_mode', $event)" />
                <small class="text-muted d-block mt-1" v-if="(value.transform_mode || 'row') === 'row'">
                    Modifier directement : <code>data.col = JSON.parse(data.col).name</code>
                </small>
                <small class="text-muted d-block mt-1" v-else>
                    Filtrer / regrouper : <code>return data.filter(r => r.statut === 'actif')</code>
                </small>
            </div>
            <ace-editor ref="aceEditor" :code="value.transform_code || ''" :height="160" mode="javascript" @update="emit('transform_code', $event)" class="mt-1" />
        </template>

        <!-- ── JSON ──────────────────────────────────────────── -->
        <hr class="my-2" />
        <b-button size="sm" variant="outline-secondary" @click="showJson = !showJson">
            <i class="fas fa-code"></i> {{ showJson ? 'Masquer le JSON' : 'Copier / Coller le JSON' }}
        </b-button>
        <div v-if="showJson" class="mt-2">
            <b-textarea v-model="jsonText" rows="6" class="font-monospace" style="font-size:0.8em" />
            <div class="mt-1">
                <b-button size="sm" variant="outline-primary" @click="copyJson">Copier</b-button>
                <b-button size="sm" variant="outline-success" class="ml-1" @click="applyJson">Appliquer</b-button>
                <small v-if="jsonError" class="text-danger ml-2">{{ jsonError }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import AceEditor from './ace_editor.vue';
import PromptGenerator from './PromptGenerator.vue';
import ConfigSaveLoad from './ConfigSaveLoad.vue';
import store from '../store';

// Schéma JSON strict (OpenAI Structured Outputs) pour la configuration d'un graphique.
// Règles strict mode : additionalProperties:false, tous les champs dans required.
const CHART_JSON_SCHEMA = {
    type: 'object',
    properties: {
        type:              { type: 'string', enum: ['pie', 'line', 'area', 'column', 'polar', 'radar', 'bubble'] },
        data_mode:         { type: 'string', enum: ['count', 'sum', 'value'] },
        label_col:         { type: 'string' },
        date_trunc:        { type: 'boolean' },
        date_trunc_unit:   { type: 'string', enum: ['minute', 'hour', 'day', 'month', 'year'] },
        value_col:         { type: 'string' },
        radius_col:        { type: 'string' },
        bubble_label_col:  { type: 'string' },
        series_col:        { type: 'string' },
        smooth:            { type: 'boolean' },
        pie_max_slices:    { type: 'number' },
        stacked:           { type: 'boolean' },
        title:             { type: 'string' },
        pre_text:          { type: 'string' },
        comment:           { type: 'string' },
        transform_mode:    { type: 'string', enum: ['row', 'all'] },
        transform_code:    { type: 'string' }
    },
    additionalProperties: false,
    required: ['type', 'data_mode', 'label_col', 'date_trunc', 'date_trunc_unit',
               'value_col', 'radius_col', 'bubble_label_col', 'series_col', 'smooth', 'pie_max_slices', 'stacked', 'title', 'pre_text',
               'comment', 'transform_mode', 'transform_code']
};

export const defaultChartConfig = () => ({
    type: 'pie',
    data_mode: 'count',
    label_col: '',
    date_trunc: false,
    date_trunc_unit: 'day',
    value_col: '',
    radius_col: '',
    bubble_label_col: '',
    series_col: '',
    smooth: true,
    pie_max_slices: 25,
    stacked: false,
    title: '',
    pre_text: '',
    comment: '',
    transform_mode: 'row',
    transform_code: ''
});

export default {
    name: 'ChartConfigForm',
    components: { AceEditor, PromptGenerator, ConfigSaveLoad },
    props: {
        value:          { type: Object, required: true },
        columns:        { type: Array,  default: () => [] },
        tableStructure: { type: Array,  default: null },
        context:        { type: String, default: null }
    },
    data() {
        return {
            uid: Math.random().toString(36).slice(2),
            transformEnabled: !!(this.value && this.value.transform_code),
            chartJsonSchema: CHART_JSON_SCHEMA,
            aiButtonLabel: 'Générer avec l\'IA',
            aiIncludeExamples: true,
            aiSendCurrentConfig: false,
            showJson: false,
            jsonText: '',
            jsonError: ''
        };
    },
    watch: {
        // Quand l'IA génère un transform_code, l'afficher automatiquement
        'value.transform_code'(val) {
            if (val && !this.transformEnabled)
                this.transformEnabled = true;
        },
        // Synchroniser la zone JSON quand la config change depuis l'extérieur
        value: {
            deep: true,
            handler(val) {
                if (this.showJson)
                    this.jsonText = JSON.stringify(val, null, 2);
            }
        },
        context() {},
        showJson(val) {
            if (val) {
                this.jsonText = JSON.stringify(this.value, null, 2);
                this.jsonError = '';
            }
        }
    },
    computed: {
        _canGenerateIa() {
            const rules = store.state.user ? store.state.user.rules : undefined;
            if (rules === null) return true; // admin
            return !!(rules && rules.generate_ia);
        }
    },
    methods: {
        autoLoad() {
            if (this.$refs.configSaveLoad) return this.$refs.configSaveLoad.autoLoad();
        },
        resetConfig() {
            if (this.$refs.configSaveLoad) this.$refs.configSaveLoad.reset();
        },
        copyJson() {
            this.jsonText = JSON.stringify(this.value, null, 2);
            navigator.clipboard && navigator.clipboard.writeText(this.jsonText);
        },
        applyJson() {
            this.jsonError = '';
            try {
                const cleaned = this.jsonText
                    .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')   // caractères invisibles / zero-width
                    .replace(/\u00A0|\u202F|\u2009|\u2008|\u2007|\u2006|\u2005|\u2004|\u2003|\u2002|\u2001/g, ' ') // espaces non-standard
                    .replace(/[\u201C\u201D\u201E\u201F\u275D\u275E\uFF02]/g, '"')  // guillemets doubles typographiques
                    .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A]/g, "'");       // guillemets simples typographiques
                const parsed = JSON.parse(cleaned);
                this.$emit('input', { ...this.value, ...parsed });
            } catch (e) {
                // Aide au diagnostic : affiche les codes des 20 premiers caractères non-ASCII
                const suspicious = [...this.jsonText.slice(0, 50)]
                    .filter(c => c.charCodeAt(0) > 127)
                    .map(c => 'U+' + c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0'));
                const hint = suspicious.length > 0 ? ` (caractères suspects : ${suspicious.join(', ')})` : '';
                this.jsonError = 'JSON invalide : ' + e.message + hint;
            }
        },
        emit(key, val) {
            this.$emit('input', { ...this.value, [key]: val });
        },
        buildAiRequest(prompt) {
            var self = this;
            var schema = (this.tableStructure || []).map(function(col) {
                var line = '- ' + col.Field + ' (' + col.Type + ')';
                if (self.aiIncludeExamples && col.Exemple) line += ' — ex: ' + col.Exemple;
                return line;
            }).join('\n');

            var currentConfigBlock = '';
            if (this.aiSendCurrentConfig && this.value) {
                var cfg = {};
                var keys = ['type','data_mode','label_col','date_trunc','date_trunc_unit',
                            'value_col','series_col','smooth','title','pre_text',
                            'transform_mode','transform_code'];
                keys.forEach(function(k) { if (self.value[k] !== undefined) cfg[k] = self.value[k]; });
                currentConfigBlock = '\nConfiguration actuelle du graphique (modifier selon la demande) :\n' +
                    JSON.stringify(cfg, null, 2) + '\n';
            }

            var intro = this.aiSendCurrentConfig
                ? 'Tu es un expert en analyse de données et en visualisation.\n' +
                  'Modifie la configuration de graphique fournie ci-dessous selon la demande de l\'utilisateur.\n'
                : 'Tu es un expert en analyse de données et en visualisation.\n' +
                  'Génère la configuration d\'un graphique à partir de la structure de la table SQL ci-dessous et de la demande utilisateur.\n';

            return (
                intro +
                currentConfigBlock +
                '\nStructure de la table :\n' + schema + '\n\n' +
                '── Règles pour transform_code ──────────────────────────────────────\n' +
                'Les champs data_mode="count" et data_mode="sum" font des agrégations simples (COUNT ou SUM + GROUP BY).\n' +
                'Pour toute agrégation plus complexe (moyenne, médiane, ratio, etc.), utilise transform_code avec transform_mode="all".\n' +
                '\n' +
                'Avec transform_mode="all" :\n' +
                '  - data est le tableau complet des lignes brutes (Array d\'objets)\n' +
                '  - Tu dois retourner un nouveau tableau avec les colonnes label_col et value_col\n' +
                '  - Exemple pour une moyenne du montant par jour :\n' +
                '      var groups = {};\n' +
                '      data.forEach(function(r) {\n' +
                '        var d = (r.date_vente || \'\').substring(0, 10);\n' +
                '        if (!groups[d]) groups[d] = { sum: 0, count: 0 };\n' +
                '        groups[d].sum += parseFloat(r.montant) || 0;\n' +
                '        groups[d].count++;\n' +
                '      });\n' +
                '      return Object.keys(groups).sort().map(function(d) {\n' +
                '        return { date: d, moyenne: groups[d].sum / groups[d].count };\n' +
                '      });\n' +
                '  → Dans ce cas : label_col="date", value_col="moyenne", data_mode="value"\n' +
                '\n' +
                'Avec transform_mode="row" :\n' +
                '  - data est un objet ligne unique (pas de return nécessaire, modifier data directement)\n' +
                '  - Utiliser uniquement pour transformer/nettoyer des valeurs, pas pour agréger\n' +
                '\n' +
                'Si aucune transformation n\'est nécessaire, laisser transform_code="" et transform_mode="row".\n' +
                '────────────────────────────────────────────────────────────────────\n' +
                '\n' +
                'Demande : ' + prompt
            );
        },
        parseAiResult(raw) {
            // Avec json_schema (Structured Outputs), la réponse est directement du JSON valide.
            return JSON.parse((raw || '').trim());
        },
        validateAiResult(parsed) {
            // Le schéma strict garantit déjà la structure ; on vérifie juste le minimum métier.
            var errors = [];
            if (!parsed || typeof parsed !== 'object')
                errors.push('Réponse invalide.');
            else if (!parsed.label_col)
                errors.push('"label_col" est vide : précisez la colonne de regroupement.');
            return errors;
        },
        onAiResult(config) {
            this.$emit('input', { ...this.value, ...config });
        },
        openLoadModal() {
            this.$refs.configSaveLoad && this.$refs.configSaveLoad.openLoadModal();
        },

        onToggleTransform(checked) {
            this.transformEnabled = checked;
            if (checked) {
                const code = (this.value && this.value.transform_code) ||
                    '// data est une ligne\n// data.colonne = JSON.parse(data.colonne).name;\n';
                this.$emit('input', {
                    ...this.value,
                    transform_code: code,
                    transform_mode: (this.value && this.value.transform_mode) || 'row'
                });
            } else {
                this.$emit('input', { ...this.value, transform_code: '' });
            }
        }
    }
};
</script>
